/**
 * useRailPresence — who the rail is talking to.
 *
 * The greeting says "Good morning, Venu" or just "Good morning", and the
 * account row shows the same name with an initial for its avatar.
 *
 * It degrades to nothing rather than to filler: with no name the greeting is
 * simply "Good morning". The rail is on every screen, so invented warmth is a
 * lie the user reads fifty times a day.
 *
 * This used to also carry `dreamLine` — a sentence quoted from the user's own
 * dreams, shown above the account row as "Because you said …". The owner
 * removed it on 2026-09-07 as out of place there, so the dream lookup went
 * with it: it re-ran `getDreams({ include: 'all' })` on every navigation to
 * feed one line of decoration.
 */

import { useEffect, useState } from 'react'

export interface RailPresence {
  /** Preferred short name, or '' when unknown. */
  name: string
  /** Single uppercase letter for the avatar, or '' when unknown. */
  initial: string
  /** True once the lookup has settled, however it settled. */
  ready: boolean
}

export function useRailPresence(): RailPresence {
  const [name, setName] = useState('')
  const [ready, setReady] = useState(false)

  // `display_name` is what the user chose to be called, so it wins over the
  // legal first name when both exist.
  useEffect(() => {
    let cancelled = false
    fetch('/api/profile/personal-info', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled) return
        const info = d?.personal_info
        const preferred = (info?.display_name || info?.first_name || '').trim()
        // Only the first word: "Venu Yanamandra" in a greeting reads like a
        // form letter, and the rail is the most personal surface in the app.
        setName(preferred.split(/\s+/)[0] || '')
      })
      .catch(() => undefined)
      .finally(() => { if (!cancelled) setReady(true) })
    return () => { cancelled = true }
  }, [])

  return { name, initial: name ? name[0].toUpperCase() : '', ready }
}
